/**
 * Vesper Event Prompt Types (Shared)
 */

import type { AtmosphereType, VesperRole, VesperStage, VesperLookingFor } from './user.types';

/**
 * Cleo-extracted matching intent — what the user EXPLICITLY asked for in the
 * planning conversation. When present on a prompt, these fields override the
 * organizer's static profile axes during scoring (so "I want a founder in
 * Climate" beats whatever the organizer's own role/sector happens to be).
 *
 * Trust Score is not part of this type — it remains the final multiplier on
 * every candidate, intent-mode or not, because it gates reliability not fit.
 *
 * Any field can be null; missing fields fall back to the organizer's profile.
 */
export interface MatchIntent {
  /** "I want to meet a founder / investor / operator / mentor" */
  wantedRole?: VesperRole | null;
  /** "...at seed / Series A / etc." */
  wantedStage?: VesperStage | null;
  /** "...working in Climate / AI infra / etc." (any of these qualifies) */
  wantedSectors?: string[] | null;
  /** "...who is open to investors / co_founder / hiring / etc." (any qualifies) */
  wantedLookingFor?: VesperLookingFor[] | null;
}

/**
 * Vesper Room types — founder-shaped activity vocabulary.
 * Replaces the prior consumer-era set (movie/arcade/museum/etc.) which biased
 * Cleo toward dating-app suggestions when founders asked to plan something.
 *
 * `dinner` and `coffee` are kept because founders genuinely meet over them
 * (founder dinners, intro coffees) — but Cleo frames them in founder context
 * via the system prompt, not as casual hangouts.
 */
export type ActivityType =
  | 'pitch_practice'
  | 'investor_coffee'
  | 'peer_mastermind'
  | 'demo_review'
  | 'working_session'
  | 'hiring_chat'
  | 'topic_salon'
  | 'dinner'
  | 'coffee'
  | 'other';

export const ACTIVITY_LABELS: Record<ActivityType, string> = {
  pitch_practice:  'Pitch practice',
  investor_coffee: 'Investor coffee',
  peer_mastermind: 'Peer mastermind',
  demo_review:     'Demo review',
  working_session: 'Working session',
  hiring_chat:     'Hiring chat',
  topic_salon:     'Topic salon',
  dinner:          'Dinner',
  coffee:          'Coffee',
  other:           'Other',
};

export type EventPromptStatus =
  | 'pending'
  | 'proposed'
  | 'screening'
  | 'confirmed'
  | 'no_matches'
  | 'expired'
  | 'cancelled';
export type CandidateRole = 'primary' | 'alternate';
export type ScreeningStatus = 'pending' | 'approved' | 'declined';

export interface EventPrompt {
  id: string;
  organizerId: string;
  activityType: ActivityType;
  activityLabel: string;
  atmosphere: AtmosphereType;
  timeStart: string;
  timeEnd: string | null;
  location: { lat: number; lng: number } | null;
  locationName: string | null;
  preferredGroupSize: number;
  minGroupSize: number;
  alternatesCount: number;
  status: EventPromptStatus;
  organizerApprovedAt: string | null;
  placeId: string | null;
  eventId: string | null;
  createdAt: string;
  updatedAt: string;
  approvedPrimaryCount?: number;
  /** Cleo-extracted explicit intent for this prompt. Null when not provided
   *  or when the user didn't state preferences. */
  matchIntent?: MatchIntent | null;
  /** Cleo-voiced single sentence explaining why the matched lineup fits
   *  this room's intent. Generated server-side at matching time, persisted
   *  on the event_prompts row (migration 062). Null when generation failed
   *  or matching produced zero candidates. */
  cleoNote?: string | null;
  /** Room format — 'in_person' (default) or 'remote' (Cleo asks for a Zoom URL
   *  in the Plan flow; location may be NULL). Migration 065, Phase B4. */
  format?: 'in_person' | 'remote';
  /** Phase C2 — Zoom/Meet/Teams join URL. Server-side gated to confirmed
   *  attendees only; non-attendees get NULL even when the column is set. */
  zoomUrl?: string | null;
}

export interface EventPromptCandidate {
  id: string;
  promptId: string;
  userId: string;
  similarityScore: number;
  activityAffinity: number;
  arkzscoreWeight: number;
  finalScore: number;
  distanceMiles: number;
  role: CandidateRole;
  screeningStatus: ScreeningStatus;
  screenedAt: string | null;
  notifiedAt: string | null;
  username: string;
  displayName: string;
  trustScore: number;
  city: string | null;
  avatarUrl: string | null;
  bio: string | null;
  age: number | null;
  gender: string | null;
  interests: string[];
  hobbies: string[];
  memberSince: string;
  eventsAttended: number;
  faceVerified: boolean;
  idVerified: boolean;
  /** Tags from skillPreferences that this candidate matched on (empty if no skill request) */
  matchedSkillTags?: string[];
  /** TRUE when the organizer manually added this candidate from their bench
   *  (via add-bench-candidate). UI surfaces a distinct "added by you" treatment
   *  instead of algorithmic-fit chips. */
  manuallyAdded?: boolean;
  // ── Vesper profile axes (surfaced on the matching screen) ──────────────────
  // Note: `role` above is the candidate STATUS ('primary' | 'alternate').
  // `vesperRole` is the user's Vesper profile role (founder/investor/etc.) —
  // intentionally renamed here to avoid shadowing.
  vesperRole?: 'founder' | 'investor' | 'operator' | 'mentor' | null;
  stage?: string | null;
  currentTitle?: string | null;
  currentCompany?: string | null;
  sectorTags?: string[];
}

export interface EventPromptWithCandidates extends EventPrompt {
  candidates: EventPromptCandidate[];
  /** When matchIntent.wantedSectors was set, signals how well candidates'
   *  sectors line up. 'exact' = at least one candidate has the exact sector;
   *  'adjacent' = best match is via adjacency map; 'distant' = no meaningful
   *  sector overlap. null = no sector intent specified. UI uses this for a
   *  soft "no exact match — adjacent fits" disclaimer. */
  sectorMatchQuality?: 'exact' | 'adjacent' | 'distant' | null;
}

export interface GroupFormationSummary {
  promptId: string;
  status: EventPromptStatus;
  preferredGroupSize: number;
  minGroupSize: number;
  alternatesCount: number;
  approvedPrimaryCount: number;
  declinedPrimaryCount: number;
  pendingPrimaryCount: number;
  approvedAlternateCount: number;
  pendingAlternateCount: number;
  meetsMinThreshold: boolean;
  meetsPreferredThreshold: boolean;
  oldestNotifiedAt: string | null;
  nonResponders: Array<{
    userId: string;
    username: string;
    role: CandidateRole;
    notifiedAt: string;
    hoursWaiting: number;
  }>;
  candidates: EventPromptCandidate[];
}

export interface CreateEventPromptPayload {
  activityType: ActivityType;
  activityLabel?: string;
  atmosphere?: AtmosphereType;
  timeStart: string;
  timeEnd?: string;
  location?: { lat: number; lng: number };
  locationName?: string;
  preferredGroupSize: number;
  minGroupSize: number;
  externalData?: Record<string, unknown>;
  agePrefMin?: number;
  agePrefMax?: number | null;
  agePrefDealbreaker?: boolean;
  placeId?: string;
  /** Cleo-extracted explicit intent (role/stage/sectors/lookingFor the user
   *  asked for). Overrides organizer profile during matching when set. */
  matchIntent?: MatchIntent | null;
  /** Room format — 'in_person' (default) or 'remote'. Phase B4 / migration 065.
   *  When 'remote', location/placeId are optional and the Plan card surfaces
   *  a Zoom variant. */
  format?: 'in_person' | 'remote';
  /** Phase C2 — Zoom (or Meet, Teams) join URL. Persisted only when format='remote'. */
  zoomUrl?: string | null;
}
